import iconHomeUoa from '../assets/art/icon-home-uoa.svg?raw'
import { HIDE_IDS, PEEKER_IDS, type PeekerId } from '../assets/art/ids'
import { ART } from '../assets/art/map'
import { svgFromRaw } from '../assets/art/svg'
import { SFX, VOICE } from '../audio/catalog'
import { el } from '../dom'
import { createPointerLatch, isFirstPointer } from '../input/single-pointer'
import {
  IDLE_PROMPT_MS,
  pickHideIndex,
  pickPeekerIndex,
  REVEAL_RESET_MS,
} from './peekaboo-logic'
import type { GameModule, Unmount } from './types'

export const peekabooGame: GameModule = {
  id: 'peekaboo',
  titleVi: 'Ú òa',
  iconSrc: iconHomeUoa,
  playable: true,
  mount(root, ctx): Unmount {
    const latch = createPointerLatch()
    let hideIndex = pickHideIndex()
    let peekerIndex = pickPeekerIndex()
    let revealing = false
    let resetTimer = 0
    let idleTimer = 0

    const stage = el('div', {
      className: 'uoa-stage',
      testId: 'game-peekaboo',
      attrs: { 'data-feedback': 'idle' },
    })

    const spots = HIDE_IDS.map((hideId, index) => {
      const character = el('div', { className: 'uoa-peeker' })
      const cover = el('div', { className: 'uoa-cover' }, [
        svgFromRaw(ART.hides[hideId], 'uoa-cover__img'),
      ])
      const spot = el(
        'button',
        {
          className: 'hide-spot',
          testId: `hide-spot-${hideId}`,
          attrs: {
            type: 'button',
            'data-hide-index': String(index),
            'data-has-peeker': index === hideIndex ? 'true' : 'false',
          },
        },
        [character, cover],
      )
      return { spot, character }
    })

    for (const item of spots) stage.append(item.spot)

    const peekerId = (): PeekerId => PEEKER_IDS[peekerIndex] ?? 'cat'

    const armIdle = () => {
      window.clearTimeout(idleTimer)
      idleTimer = window.setTimeout(() => {
        if (revealing) return
        ctx.audio.play(VOICE.dauRoiNhi)
      }, IDLE_PROMPT_MS)
    }

    const paintPeekers = () => {
      const raw = ART.peekers[peekerId()]
      for (const [index, item] of spots.entries()) {
        item.character.replaceChildren(svgFromRaw(raw, 'uoa-peeker__img'))
        item.spot.dataset.hasPeeker = index === hideIndex ? 'true' : 'false'
        item.spot.classList.remove('is-revealed')
      }
    }

    const resetRound = () => {
      revealing = false
      hideIndex = pickHideIndex()
      peekerIndex = pickPeekerIndex(peekerIndex)
      paintPeekers()
      stage.dataset.feedback = 'idle'
      armIdle()
    }

    const rustle = () => {
      ctx.audio.play(SFX.whoosh)
      if (stage.dataset.feedback !== 'reveal') stage.dataset.feedback = 'rustle'
    }

    const reveal = (index: number) => {
      if (revealing) return
      revealing = true
      const item = spots[index]
      if (item) item.spot.classList.add('is-revealed')
      stage.dataset.feedback = 'reveal'
      ctx.audio.play(SFX.whoosh)
      ctx.audio.play(SFX.giggle)
      ctx.audio.play(VOICE.uOa)
      window.clearTimeout(resetTimer)
      resetTimer = window.setTimeout(resetRound, REVEAL_RESET_MS)
    }

    const onDown = (event: PointerEvent) => {
      if (!isFirstPointer(event, latch)) return
      event.preventDefault()
      try {
        stage.setPointerCapture(event.pointerId)
      } catch {
        // capture can fail on some synthetic events
      }
      armIdle()
      const target = event.target
      if (!(target instanceof Element)) {
        rustle()
        return
      }
      const spot = target.closest('.hide-spot')
      if (!(spot instanceof HTMLElement)) {
        rustle()
        return
      }
      const index = Number(spot.dataset.hideIndex)
      if (index === hideIndex && !revealing) reveal(index)
      else rustle()
    }

    const onUp = (event: PointerEvent) => {
      latch.release(event.pointerId)
    }

    stage.addEventListener('pointerdown', onDown)
    stage.addEventListener('pointerup', onUp)
    stage.addEventListener('pointercancel', onUp)

    root.append(stage)
    paintPeekers()
    armIdle()

    return () => {
      window.clearTimeout(resetTimer)
      window.clearTimeout(idleTimer)
      latch.reset()
      stage.removeEventListener('pointerdown', onDown)
      stage.removeEventListener('pointerup', onUp)
      stage.removeEventListener('pointercancel', onUp)
      root.replaceChildren()
    }
  },
}
